// =====================================================================
// src/lib/escalas.js — o que a tela de escalas DECIDE, sem DOM
//
// A escala resolvida (padrão − folgas + coberturas) é do banco
// (app.escala_do_dia, 0009). Aqui: a grade dia-da-semana × andar a partir
// das linhas de escalas_padrao, os andares do hotel a partir do quadro, as
// exceções de uma data, e datas. Puro, testado em scripts/teste_tela.mjs.
// =====================================================================

export const DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
export const DIAS_CURTOS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// yyyy-mm-dd no fuso local (o banco compara com current_date do servidor;
// a data que a tela manda é a que a supervisora está vendo).
export function dataISO(d) {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function diaDaSemana(iso) {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(a, m - 1, d).getDay();
}

export function somarDias(iso, n) {
  const [a, m, d] = iso.split('-').map(Number);
  return dataISO(new Date(a, m - 1, d + n));
}

export function dataCurta(iso) {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(a, m - 1, d).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

// Os andares do hotel, em ordem, a partir das linhas do quadro (um quarto
// por linha). Quartos sem andar não entram: não se escala "sem andar".
export function andaresDoHotel(quadro) {
  const s = new Set();
  for (const q of quadro) if (q.andar !== null && q.andar !== undefined) s.add(Number(q.andar));
  return [...s].sort((a, b) => a - b);
}

// A grade: uma linha por andar MAIS a linha "Volantes" (andar nulo) no
// topo; 7 células (dom..sáb) com as pessoas escaladas
// [{id, usuario_id, nome, quarto_de, quarto_ate}] em ordem de nome.
export const VOLANTE = null;
export function grade(padrao, andares, equipe) {
  const nome = new Map(equipe.map(p => [p.id, p.nome]));
  const linha = andar => ({
    andar,
    rotulo: andar === VOLANTE ? 'Volantes' : `${andar}º andar`,
    dias: DIAS.map((_, dia) =>
      padrao.filter(l => (andar === VOLANTE ? l.andar === null || l.andar === undefined : Number(l.andar) === andar) && Number(l.dia_semana) === dia)
        .map(l => ({ id: l.id, usuario_id: l.usuario_id, nome: nome.get(l.usuario_id) || '(sem nome)', quarto_de: l.quarto_de || null, quarto_ate: l.quarto_ate || null }))
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))),
  });
  return [linha(VOLANTE), ...andares.map(linha)];
}

// "Ana (401–420)" ou só "Ana".
export function nomeComFaixa(p) {
  return p.quarto_de ? `${p.nome} (${p.quarto_de}–${p.quarto_ate})` : p.nome;
}

// Os quartos de um andar, em ordem natural, para escolher a faixa.
export function quartosDoAndar(quadro, andar) {
  return quadro.filter(q => Number(q.andar) === andar).map(q => q.numero)
    .sort((a, b) => String(a).localeCompare(String(b), 'pt-BR', { numeric: true }));
}

// Quem ainda pode ser escalado numa célula: a equipe menos quem já está.
export function disponiveis(celula, equipe) {
  const ja = new Set(celula.map(p => p.usuario_id));
  return equipe.filter(p => !ja.has(p.id));
}

// As exceções de uma data com nome, folgas antes de coberturas, por nome.
export function excecoesDoDia(excecoes, iso, equipe) {
  const nome = new Map(equipe.map(p => [p.id, p.nome]));
  return excecoes.filter(x => x.data === iso)
    .map(x => ({ ...x, nome: nome.get(x.usuario_id) || '(sem nome)' }))
    .sort((a, b) => (a.tipo === b.tipo ? 0 : a.tipo === 'folga' ? -1 : 1) || a.nome.localeCompare(b.nome, 'pt-BR'));
}

export function descreverExcecao(x) {
  const semAndar = x.andar === null || x.andar === undefined;
  const motivo = x.motivo ? ` (${x.motivo})` : '';
  if (x.tipo === 'folga') return `${x.nome} — folga${semAndar ? ' o dia inteiro' : ` no ${x.andar}º andar`}${motivo}`;
  if (semAndar) return `${x.nome} — cobre como volante${motivo}`;
  return `${x.nome} — cobre o ${x.andar}º andar${x.quarto_de ? ` (${x.quarto_de}–${x.quarto_ate})` : ''}${motivo}`;
}

// A escala resolvida de uma data (linhas de public.escala) por andar, volantes
// primeiro: [{andar: null, rotulo: 'Volantes', pessoas}, {andar: 2, ...}].
export function escalaPorAndar(linhas) {
  const mapa = new Map();
  for (const l of linhas) {
    const k = l.andar === null || l.andar === undefined ? null : Number(l.andar);
    if (!mapa.has(k)) mapa.set(k, []);
    mapa.get(k).push(l);
  }
  return [...mapa.keys()].sort((a, b) => (b === null) - (a === null) || a - b).map(andar => ({
    andar,
    rotulo: andar === null ? 'Volantes' : `${andar}º andar`,
    pessoas: mapa.get(andar).slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
  }));
}
